import { IconInfoType } from "@/sections/FaqSections";
import React, { useEffect, useState } from "react";

interface SvgIconProps {
  icon: IconInfoType;
  className?: string;
  style?: React.CSSProperties;
}

export const SvgIcon: React.FC<SvgIconProps> = ({ 
  icon, 
  className = "",
  style = {}
}) => {
  const [svgContent, setSvgContent] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isDecorative = icon?.data?.additionalInfo?.customData?.isDecorative;
  const altText = icon?.data?.additionalInfo?.customData?.translations?.it?.altText || "Icona";

  useEffect(() => {
    if (!icon?.url) {
      setError(true);
      setIsLoading(false);
      return;
    }

    const fetchSvg = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(icon.url!);
        if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch SVG`);
        
        const svgText = await response.text();
        
        // Basic validation to ensure it's actually SVG content
        if (!svgText.includes('<svg')) {
          throw new Error('Response is not valid SVG content');
        }
        
        setSvgContent(svgText);
        setError(false);
      } catch (err) {
        console.error('Error fetching SVG:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSvg();
  }, [icon?.url]);

  const containerStyle = {
    width: icon?.size?.width ?? 40,
    height: icon?.size?.height ?? 40,
    ...(icon?.position?.x !== undefined || icon?.position?.y !== undefined
      ? {
          transform: `translate(${icon?.position?.x ?? 0}px, ${icon?.position?.y ?? 0}px)`,
        }
      : {}),
    ...style
  };

  // Show loading state or fallback while fetching
  if (isLoading || error || !svgContent) {
    return (
      <img
        src={icon?.url || ""}
        alt={isDecorative ? "" : altText}
        aria-hidden={isDecorative}
        width={icon?.size?.width ?? 40}
        height={icon?.size?.height ?? 40}
        className={className}
        style={{ 
          opacity: isLoading ? 0.5 : 1,
          transition: 'opacity 0.2s ease-in-out',
          ...containerStyle
        }}
      />
    );
  }

  // For decorative images, hide from screen readers
  if (isDecorative) {
    return (
      <div
        className={className}
        style={containerStyle}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    );
  }

  // For non-decorative images, include aria-label
  return (
    <div
      className={className}
      style={containerStyle}
      role="img"
      aria-label={altText}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};